import type { Ref } from "@/shared/utils/ref";

export type Listener<T> = (value: T) => void;
export type Listeners<T> = Set<Listener<T>>;
export type RemoveListener = () => void;
export type Listen<T> = (listener: Listener<T>) => RemoveListener;

export interface BaseSignal<T> {
    notify: (value: T) => void;
    listen: Listen<T>;
}

export interface Signal<T> extends BaseSignal<T> {
    listenAndInit: (listener: Listener<T>, initialValue: T) => RemoveListener;
}

export const createSignal = <T>(): Signal<T> => {
    const listeners: Listeners<T> = new Set();

    const notify = (value: T) => {
        for (const listener of listeners) {
            listener(value);
        }
    };

    const listen: Listen<T> = (listener) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
    };

    return {
        notify,
        listen,
        listenAndInit: (listener, initialValue) => {
            listener(initialValue);
            return listen(listener);
        },
    };
};

export interface SignalWithValue<T> extends BaseSignal<T> {
    value: Readonly<T>;
    listenAndInit: (listener: Listener<T>) => RemoveListener;
}

export const createSignalWithValue = <T>(initialValue: T): SignalWithValue<T> => {
    let value = initialValue;
    const signal = createSignal<T>();
    signal.listen((newValue) => (value = newValue));

    return {
        ...signal,
        get value(): T {
            return value;
        },
        listenAndInit: (listener) => {
            // Initialize with the CURRENT value: late subscribers must not
            // see the stale construction-time value.
            listener(value);
            return signal.listen(listener);
        },
    };
};

export const createSignalWithRef = <T>(initialValue: T): SignalWithValue<T> => {
    const ref: Ref<T> = { value: initialValue };
    const signal = createSignal<T>();
    signal.listen((newValue) => (ref.value = newValue));

    return {
        get value(): T {
            return ref.value;
        },
        listen: signal.listen,
        notify: (newValue) => {
            ref.value = newValue;
            signal.notify(ref.value);
        },
        listenAndInit: (listener) => {
            listener(ref.value);
            return signal.listen(listener);
        },
    };
};

// For Testing
// -----------------------------------------------------------------------------
export interface MockSignal<T> extends Signal<T> {
    callCount: number;
    lastValue: T | undefined;
}

export const createMockSignal = <T>(): MockSignal<T> => {
    let callCount = 0;
    let lastValue: T | undefined;
    return {
        get callCount() {
            return callCount;
        },
        get lastValue() {
            return lastValue;
        },
        notify: (value: T) => {
            callCount += 1;
            lastValue = value;
        },
        listen: () => () => {
            /* noop */
        },
        listenAndInit: (listener, initialValue) => () => {
            listener(initialValue);
        },
    };
};
